/**
 * Welcome / Onboarding page - Slice 2.2 (2026-05-20).
 *
 * The first surface an IT Admin sees after activation. Federated
 * checklist pattern (Atlassian Access / Vanta-style). Reads from the
 * Slice 2.1 backend at GET /v1/onboarding/journey; PATCHes card status
 * via the same module's PATCH endpoint.
 *
 * Cards auto-resolve when the backend's event listeners fire (e.g.,
 * "first connector test succeeded" triggers
 * POST /v1/onboarding/cards/connect_data_sources/auto-resolve internally).
 * The UI just renders whatever the API returns - auto-resolved cards
 * show a "completed automatically" badge.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Archive,
  Bell,
  Building,
  CheckCircle2,
  Circle,
  Clock,
  Database,
  Globe,
  Loader2,
  Lock,
  PartyPopper,
  Shield,
  Siren,
  Sparkles,
  Users,
  UserPlus,
  Zap,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  CARD_META,
  getJourney,
  updateCard,
  type OnboardingCardRow,
  type OnboardingJourney,
  type OnboardingStatus,
} from '@/lib/onboarding-api';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  brand: Sparkles,
  shield: Shield,
  users: Users,
  building: Building,
  lock: Lock,
  database: Database,
  siren: Siren,
  archive: Archive,
  bell: Bell,
  'user-plus': UserPlus,
  globe: Globe,
};

function StatusBadge({ row }: { row: OnboardingCardRow }): JSX.Element {
  if (row.status === 'completed') {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        {row.autoResolved ? 'Completed automatically' : 'Completed'}
      </Badge>
    );
  }
  if (row.status === 'in_progress') {
    return (
      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
        <Clock className="h-3 w-3 mr-1" />
        In progress
      </Badge>
    );
  }
  if (row.status === 'skipped') {
    return (
      <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">Skipped</Badge>
    );
  }
  return (
    <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
      <Circle className="h-3 w-3 mr-1" />
      Pending
    </Badge>
  );
}

interface CardRowProps {
  row: OnboardingCardRow;
  onStart: (cardId: string) => void;
  onComplete: (cardId: string) => void;
  onSkip: (cardId: string) => void;
  busy: boolean;
}

function CardRow({ row, onStart, onComplete, onSkip, busy }: CardRowProps): JSX.Element {
  const meta = CARD_META[row.cardId] ?? {
    title: row.cardId,
    description: '',
    estimateMinutes: 1,
    iconKey: 'brand',
    route: '/welcome', // safe fallback for unknown card IDs
  };
  const Icon = ICON_MAP[meta.iconKey] ?? Sparkles;
  const isCompleted = row.status === 'completed';
  const isSkipped = row.status === 'skipped';
  const isInProgress = row.status === 'in_progress';

  // Phase 0 item 7 (2026-05-26) - Replaced hardcoded `text-white` /
  // `text-slate-*` / `bg-slate-*` classes (which broke the page in light
  // mode) with theme tokens (`text-foreground` / `text-muted-foreground`
  // / `bg-card` / `border-border`). Now renders correctly in both light
  // and dark modes.
  return (
    <div
      className={`group rounded-lg border p-4 transition-all ${
        isCompleted
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : isInProgress
            ? 'border-amber-500/30 bg-amber-500/5'
            : isSkipped
              ? 'border-border/30 bg-muted/30 opacity-60'
              : 'border-border/50 bg-card/30 hover:border-border'
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            isCompleted ? 'bg-emerald-500/20' : isInProgress ? 'bg-amber-500/20' : 'bg-muted'
          }`}
        >
          {isCompleted ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
          ) : (
            <Icon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">{meta.title}</h3>
            <StatusBadge row={row} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">{meta.description}</p>
          <p className="text-[11px] text-muted-foreground/70 mt-1">~{meta.estimateMinutes} min</p>

          {!isCompleted && !isSkipped && (
            <div className="flex items-center gap-2 mt-3">
              {!isInProgress && (
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 text-xs"
                  disabled={busy}
                  onClick={() => onStart(row.cardId)}
                  title={`Open ${meta.title} setup page`}
                >
                  Start
                </Button>
              )}
              {isInProgress && (
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                  disabled={busy}
                  onClick={() => onComplete(row.cardId)}
                >
                  Mark complete
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                disabled={busy}
                onClick={() => onSkip(row.cardId)}
              >
                Skip
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Welcome(): JSX.Element {
  const [journey, setJourney] = useState<OnboardingJourney | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await getJourney();
      setJourney(data);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load onboarding journey');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Poll every 30s - picks up auto-resolution from backend event listeners
    // (Vanta pattern: connector tests, SSO assertions, SCIM syncs flip cards).
    const id = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const handleStatusChange = useCallback(
    async (cardId: string, status: OnboardingStatus) => {
      setBusy(true);
      try {
        await updateCard(cardId, status);
        await refresh();
      } catch (err) {
        setError((err as Error).message ?? 'Failed to update card');
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  // Phase 0 item 4 (2026-05-26) - Start button now navigates to the
  // actual setup page (per CARD_META[cardId].route) in addition to
  // PATCHing status=in_progress. Previously the click only flipped the
  // status state, leaving the operator stranded on the Welcome page
  // wondering what "Start" did.
  const handleStart = useCallback(
    async (cardId: string) => {
      const meta = CARD_META[cardId];
      // Mark in_progress in the background (don't block navigation).
      void handleStatusChange(cardId, 'in_progress');
      if (meta?.route) {
        void navigate({ to: meta.route });
      }
    },
    [handleStatusChange, navigate],
  );

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Welcome to FOCTTA" subtitle="Onboarding checklist" />
        <GlassCard>
          <CardHeader>
            <CardTitle className="text-red-400">Failed to load journey</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </GlassCard>
      </PageContainer>
    );
  }

  if (!journey || journey.cards.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="Welcome to FOCTTA" subtitle="No onboarding checklist yet" />
      </PageContainer>
    );
  }

  const allComplete = journey.completedCards === journey.totalCards;

  return (
    <PageContainer>
      <PageHeader
        title="Welcome to FOCTTA"
        subtitle="Let's get your workspace ready"
      />

      <GlassCard className="mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                {allComplete ? (
                  <PartyPopper className="h-5 w-5 text-emerald-400" />
                ) : (
                  <Zap className="h-5 w-5 text-amber-400" />
                )}
                Setup progress
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {journey.completedCards} of {journey.totalCards} cards complete
                {journey.inProgressCards > 0 ? ` · ${journey.inProgressCards} in progress` : ''}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-foreground">{journey.percentComplete}%</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">complete</div>
            </div>
          </div>
          {/* OBS-14 fix (2026-05-26) - two-tone bar so "3 in progress" no
              longer reads as a contradiction with a 0% bar. Solid colour
              shows the strictly-complete fraction (matches the headline
              percentage); a lighter overlay extends the bar for cards in
              `in_progress` so the user sees that work has actually
              started. The right-side headline number stays strict
              %-complete for honesty. */}
          <div className="h-2 bg-muted rounded-full overflow-hidden flex">
            <div
              className={`h-full transition-all duration-500 ${
                allComplete
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                  : 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500'
              }`}
              style={{ width: `${journey.percentComplete}%` }}
            />
            {journey.inProgressCards > 0 && journey.totalCards > 0 && !allComplete && (
              <div
                className="h-full transition-all duration-500 bg-gradient-to-r from-blue-500/35 to-indigo-500/35"
                style={{
                  width: `${Math.round((journey.inProgressCards / journey.totalCards) * 100)}%`,
                }}
                title={`${journey.inProgressCards} card${journey.inProgressCards === 1 ? '' : 's'} in progress`}
              />
            )}
          </div>
        </div>
      </GlassCard>

      <div className="space-y-3" data-testid="onboarding-cards">
        {journey.cards.map((row) => (
          <CardRow
            key={row.cardId}
            row={row}
            busy={busy}
            onStart={(id) => void handleStart(id)}
            onComplete={(id) => void handleStatusChange(id, 'completed')}
            onSkip={(id) => void handleStatusChange(id, 'skipped')}
          />
        ))}
      </div>

      {allComplete && (
        <GlassCard className="mt-6 border-emerald-500/30">
          <div className="p-6 text-center">
            <PartyPopper className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground">
              Your workspace is fully set up
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              All onboarding cards are complete. You can revisit any step from your settings later.
            </p>
          </div>
        </GlassCard>
      )}
    </PageContainer>
  );
}
